/**
 * 인증 상태 관리 Context
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import api from '../services/api';

const AuthContext = createContext(null);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://192.168.0.113:59000/api/v1';

// Access Token은 메모리에만 보관 (localStorage 사용 금지)
let accessToken = null;

export const getAccessToken = () => accessToken;
export const setAccessToken = (token) => { accessToken = token; };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = useCallback(async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    const { access_token, user: userData } = response.data;
    accessToken = access_token;
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // 로그아웃 실패해도 로컬 상태는 정리
    }
    accessToken = null;
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      // 인터셉터를 거치지 않도록 raw axios 사용
      const response = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true }
      );

      const token = response.data?.data?.access_token;
      if (!token) {
        throw new Error('No access token');
      }
      accessToken = token;

      // 사용자 정보 조회 (raw axios)
      const meResponse = await axios.get(`${API_BASE_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const userData = meResponse.data?.user;
      if (!userData) {
        throw new Error('No user data');
      }
      // 운영툴은 ADMIN 계정 사용 금지 — 같은 도메인에서 admin 쿠키가 공유돼도 차단
      if (userData.role === 'ADMIN') {
        accessToken = null;
        setUser(null);
        return null;
      }
      setUser(userData);
      return token;
    } catch (e) {
      accessToken = null;
      setUser(null);
      return null;
    }
  }, []);

  // 앱 시작 시 Refresh Token으로 자동 로그인 시도
  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // 화면 활성화 시 인증 상태 재확인 (탭 복귀)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !loading) {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refresh, loading]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
